import React from 'react';
import '../styles/PlayerInfoPanel.css';

// Helper to check if string looks like encoded data (not a valid avatar)
const looksLikeEncodedData = (str) => {
  if (!str || typeof str !== 'string') return false;
  // Only check for base64-like strings that are NOT URLs
  if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:image')) {
    return false; // Valid URLs and data URIs
  }
  // Check for base64 encoded strings (long alphanumeric strings)
  if (str.length > 50 && /^[A-Za-z0-9+/=]+$/.test(str)) return true;
  return false;
};

// Helper to get safe avatar - properly handles URLs and emojis
const getSafeAvatar = (avatar) => {
  if (!avatar) return '👤';
  if (typeof avatar !== 'string') return '👤';
  
  // Check if it's a valid URL (http/https)
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
    return avatar;
  }
  
  // Check if it's a data URI (base64 image)
  if (avatar.startsWith('data:image')) {
    return avatar;
  }
  
  // Check if it looks like encoded garbage data
  if (looksLikeEncodedData(avatar)) {
    return '👤';
  }
  
  // Emoji avatars (1-4 characters)
  if (avatar.length <= 4) {
    return avatar;
  }
  
  // Default fallback
  return '👤';
};

const PlayerInfoPanel = ({ 
  whitePlayer, 
  blackPlayer, 
  currentTurn, 
  isSpectator,
  playerColor,
  moveNumber,
  showStats = false,
  spectatorCount = 0
}) => {
  const whiteAvatar = getSafeAvatar(whitePlayer?.avatar);
  const blackAvatar = getSafeAvatar(blackPlayer?.avatar);
  
  const isWhiteTurn = currentTurn === 'white';
  const isBlackTurn = currentTurn === 'black';

  // Calculate win rate helper
  const getWinRate = (player) => {
    if (!player?.wins || !player?.gamesPlayed) return null;
    return Math.round((player.wins / player.gamesPlayed) * 100);
  };

  // Check if avatar is an image URL
  const isImageAvatar = (avatar) => {
    return avatar && (avatar.startsWith('data:image') || avatar.startsWith('http'));
  };

  return (
    <div className="player-info-container" data-testid="player-info-container">
      {/* White Player (Left) */}
      <div 
        className={`player-info-box white-player ${isWhiteTurn ? 'active-turn' : ''}`}
        data-testid="white-player-info"
      >
        <div className={`player-avatar white-avatar ${isImageAvatar(whiteAvatar) ? 'has-image' : ''}`}>
          {isImageAvatar(whiteAvatar) ? (
            <img src={whiteAvatar} alt={whitePlayer?.name || 'White'} />
          ) : (
            <span className="avatar-emoji">{whiteAvatar}</span>
          )}
        </div>
        <div className="player-details">
          <span className="player-name" title={whitePlayer?.name || 'White Player'}>
            {whitePlayer?.name || 'White Player'}
          </span>
          <span className="player-color-badge white-badge">White</span>
          {showStats && whitePlayer?.gamesPlayed > 0 && (
            <div className="player-stats">
              {whitePlayer?.rating && (
                <span className="player-stat rating">⭐ {whitePlayer.rating}</span>
              )}
              {getWinRate(whitePlayer) !== null && (
                <span className="player-stat win-rate">{getWinRate(whitePlayer)}% Win</span>
              )}
            </div>
          )}
        </div>
        {isWhiteTurn && <div className="turn-indicator" aria-label="White's turn">▶</div>}
      </div>

      {/* Game Status Center */}
      <div className="game-status-center">
        <div className="vs-text">VS</div>
        {moveNumber > 0 && (
          <div className="move-counter">Move {moveNumber}</div>
        )}
        {spectatorCount > 0 && (
          <div className="spectator-count" title={`${spectatorCount} spectator${spectatorCount > 1 ? 's' : ''} watching`}>
            👁️ {spectatorCount}
          </div>
        )}
      </div>

      {/* Black Player (Right) */}
      <div 
        className={`player-info-box black-player ${isBlackTurn ? 'active-turn' : ''}`}
        data-testid="black-player-info"
      >
        {isBlackTurn && <div className="turn-indicator" aria-label="Black's turn">◀</div>}
        <div className="player-details">
          <span className="player-name" title={blackPlayer?.name || 'Black Player'}>
            {blackPlayer?.name || 'Black Player'}
          </span>
          <span className="player-color-badge black-badge">Black</span>
          {showStats && blackPlayer?.gamesPlayed > 0 && (
            <div className="player-stats">
              {blackPlayer?.rating && (
                <span className="player-stat rating">⭐ {blackPlayer.rating}</span>
              )}
              {getWinRate(blackPlayer) !== null && (
                <span className="player-stat win-rate">{getWinRate(blackPlayer)}% Win</span>
              )}
            </div>
          )}
        </div>
        <div className={`player-avatar black-avatar ${isImageAvatar(blackAvatar) ? 'has-image' : ''}`}>
          {isImageAvatar(blackAvatar) ? (
            <img src={blackAvatar} alt={blackPlayer?.name || 'Black'} />
          ) : (
            <span className="avatar-emoji">{blackAvatar}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerInfoPanel;
